import React, { useEffect, useState, useRef, useMemo } from "react";
import { useReactMediaRecorder } from "react-media-recorder";
import { API_ROOT } from "../../../../../baseUrls";
import { IconButton, FormControl } from "@mui/material";
import { Videocam, Stop } from "@mui/icons-material";

const ENCODING_CANDIDATES = [
  "video/mp4;codecs=avc1.42E01E,mp4a.40.2", // Safari/iOS prefers H.264 + AAC
  "video/mp4",                               // generic mp4
  "video/webm;codecs=vp9,opus",              // Chrome/Firefox modern
  "video/webm;codecs=vp8,opus",
  "video/webm"                               // broad fallback
];

// Minimal mime→extension map (add more if you need)
const MIME_TO_EXT = {
  "video/mp4": "mp4",
  "video/webm": "webm",
  "video/ogg": "ogv",
  "video/quicktime": "mov"
};

function pickSupportedMime(candidates) {
  if (typeof window === "undefined" || typeof window.MediaRecorder === "undefined") return undefined;
  for (const cand of candidates) {
    if (MediaRecorder.isTypeSupported?.(cand)) return cand;
  }
  return undefined; // let browser choose if none explicitly supported
}

async function fetchBlobAndUpload({
  blobUrl,
  setRecording,
  onStop,
  mindfileId,
  emotion,
  folderId,
  authenticity_token,
  fetchItems
}) {
  const response = await fetch(blobUrl);
  const blob = await response.blob();

  // Normalize mime to a base type (strip codecs if present in some UAs)
  const baseMime = (blob.type || "").split(";")[0];
  const ext = MIME_TO_EXT[baseMime] || "webm"; // safe default

  const formData = new FormData();
  formData.append("recording[type]", "Video");
  formData.append("recording[ext]", ext);
  formData.append("recording[emotion]", emotion);
  if(folderId){
    formData.append("folder_id", folderId);
  }
  formData.append("fileData", blob, `recording.${ext}`);

  fetch(`${API_ROOT}/mindfiles/${mindfileId}/upload-recording`, {
    method: "POST",
    credentials: "include",
    body: formData,
    headers: { "X-CSRF-Token": authenticity_token }
  })
    .then(() => onStop(blobUrl, blob))
    .then(() => setRecording(false))
    .then(fetchItems)
    .catch(console.error);
}

export default function VideoRecorder(props) {
  const { className, mindfileId, emotion, folderId, authenticity_token, fetchItems, onStart, onStop } = props;

  // Pick the first supported mime once (per mount)
  const selectedMime = useMemo(() => pickSupportedMime(ENCODING_CANDIDATES), []);

  const { startRecording, stopRecording, mediaBlobUrl } = useReactMediaRecorder({
    video: {
      width: { ideal: 1280, max: 1280 },
      height: { ideal: 720, max: 720 },
      facingMode: { ideal: "user" }
    },
    // Tell MediaRecorder what mime to use (if we found one)
    mediaRecorderOptions: selectedMime ? { mimeType: selectedMime } : undefined,
    askPermissionOnMount: true,
  });

  const [recording, setRecording] = useState(false);
  const timeoutRef = useRef(null);
  const MAX_DURATION_MS = 15 * 60 * 1000; // 15 minute cap

  useEffect(() => {
    if (recording && mediaBlobUrl) {
      fetchBlobAndUpload({
        blobUrl: mediaBlobUrl,
        setRecording,
        onStop,
        mindfileId,
        emotion,
        folderId,
        authenticity_token,
        fetchItems
      });
    }
  }, [mediaBlobUrl]);

  const start = () => {
    onStart();
    startRecording();
    setRecording(true);

    // Auto-stop after max duration
    timeoutRef.current = setTimeout(() => {
      stop();
    }, MAX_DURATION_MS);
  };

  const stop = () => {
    stopRecording();

    // Clear pending auto-stop if stopped early
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <FormControl>
      <IconButton
        className={className}
        color="primary"
        onClick={() => (recording ? stop() : start())}
        aria-pressed={recording}
      >
        {recording ? <Stop /> : <Videocam />}
      </IconButton>
    </FormControl>
  );
}
